import { Dialog, DialogContent, DialogTitle } from '@crowdex/ui/components/dialog';
import { useEffect, useRef, useState } from 'react';
import { askAgent, attachToAgent } from './agent-ask';
import { AppFrame } from './app-frame';

const OPEN_DIALOG = 'crowdex:open-app-dialog';

type DialogRequest = {
  plugin: string;
  path: string;
  title: string;
  input?: unknown;
  size?: 'default' | 'wide' | undefined;
  /** The conversation whose workspace the view works in (see `AppFrame`). */
  workspace?: string | undefined;
  resolve(result: unknown): void;
};

/** Opens a view of an app in a dialog; resolves with what it closes with (`null` if dismissed). */
export function openAppDialog(request: Omit<DialogRequest, 'resolve'>): Promise<unknown> {
  return new Promise((resolve) => {
    window.dispatchEvent(
      new CustomEvent<DialogRequest>(OPEN_DIALOG, { detail: { ...request, resolve } }),
    );
  });
}

/** Shows the apps' dialogs, one at a time. */
export function AppDialogHost() {
  const [request, setRequest] = useState<DialogRequest | null>(null);
  const current = useRef<DialogRequest | null>(null);
  current.current = request;
  useEffect(() => {
    const open = (event: Event) => {
      const next = (event as CustomEvent<DialogRequest>).detail;
      current.current?.resolve(null);
      setRequest(next);
    };
    window.addEventListener(OPEN_DIALOG, open);
    return () => window.removeEventListener(OPEN_DIALOG, open);
  }, []);
  const close = (result: unknown) => {
    current.current?.resolve(result);
    setRequest(null);
  };
  return (
    <Dialog onOpenChange={(open) => !open && close(null)} open={request !== null}>
      {request !== null && (
        <DialogContent className={request.size === 'wide' ? 'max-w-4xl p-0' : 'max-w-xl p-0'}>
          <DialogTitle className="sr-only">{request.title}</DialogTitle>
          <div className="h-[620px] max-h-[80vh]">
            <AppFrame
              container="dialog"
              plugin={request.plugin}
              handlers={{
                ask: (text) => {
                  askAgent(text);
                  close(null);
                },
                attach: (selections, send) => {
                  attachToAgent(selections, send);
                  close(null);
                },
                open: () => {
                  throw new Error('A dialog cannot open other views.');
                },
                closeDialog: close,
                confirm: ({ title, message }) =>
                  window.confirm(message ? `${title}\n\n${message}` : title),
                selectItem: () => undefined,
                refreshPage: () => undefined,
              }}
              input={request.input}
              path={request.path}
              title={request.title}
              workspace={request.workspace}
            />
          </div>
        </DialogContent>
      )}
    </Dialog>
  );
}
